import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Upload, FileText, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const num = (v) => parseFloat(v) || 0;
const fmtBRL = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export const NFImportDialog = ({ open, onOpenChange, clients, onImported, defaultClientId = "" }) => {
  const [step, setStep] = useState("upload"); // upload | review
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [nfNumber, setNfNumber] = useState("");
  const [nfDate, setNfDate] = useState("");
  const [clientId, setClientId] = useState("");
  const [items, setItems] = useState([]);
  const [allMaterials, setAllMaterials] = useState([]);
  const [freight, setFreight] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [icmsSt, setIcmsSt] = useState(0);
  const [nfTotal, setNfTotal] = useState(0);
  const [updateStock, setUpdateStock] = useState(true);
  const [createBalance, setCreateBalance] = useState(true);

  useEffect(() => {
    if (open) {
      setStep("upload"); setFile(null); setParsed(null); setItems([]);
      setNfNumber(""); setNfDate(""); setClientId(defaultClientId || "");
      setFreight(0); setDiscount(0); setIcmsSt(0); setNfTotal(0);
      setUpdateStock(true); setCreateBalance(true);
      axios.get(`${API}/materials`, { withCredentials: true })
        .then(({ data }) => setAllMaterials(data)).catch(() => {});
    }
  }, [open, defaultClientId]);

  const handleParse = async () => {
    if (!clientId) { toast.error("Selecione o cliente antes de enviar a NF"); return; }
    if (!file) { toast.error("Selecione o arquivo da NF"); return; }
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/inventory/parse-nf`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setParsed(data);
      setNfNumber(data.nf_number || "");
      setNfDate(data.nf_date || "");
      setFreight(data.freight || 0);
      setDiscount(data.discount || 0);
      setIcmsSt(data.icms_st || 0);
      setNfTotal(data.nf_total || 0);
      setItems((data.items || []).map((it) => ({
        ...it,
        action: "new",
        material_id: "",
        total_quantity: 0,
        unit_value: it.unit_value || 0,
        ipi: it.ipi || 0,
        total_value: it.total_value || 0,
      })));
      setStep("review");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nao foi possivel ler a NF");
    } finally { setParsing(false); }
  };

  const updateItem = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => {
      if (i !== idx) return it;
      const next = { ...it, ...patch };
      if ("quantity" in patch || "unit_value" in patch || "ipi" in patch) {
        next.total_value = Math.round((num(next.quantity) * num(next.unit_value) + num(next.ipi)) * 100) / 100;
      }
      return next;
    }));
  };

  const clientMaterials = clientId ? allMaterials.filter((m) => m.client_id === clientId) : allMaterials;
  const itemsTotal = items.reduce((s, it) => s + num(it.total_value), 0);
  const computedTotal = Math.round((itemsTotal + num(freight) + num(icmsSt) - num(discount)) * 100) / 100;

  const handleImport = async () => {
    if (!clientId) { toast.error("Selecione o cliente (consignante) do material"); return; }
    if (!updateStock && !createBalance) { toast.error("Selecione ao menos um destino: estoque ou controle de saldo"); return; }
    const payloadItems = items.map((it) => ({
      action: it.action,
      material_id: it.action === "existing" ? it.material_id : "",
      name: it.description,
      code: it.code,
      unit: it.unit || "unidade",
      category: "outro",
      quantity: num(it.quantity),
      total_quantity: num(it.total_quantity),
      unit_value: num(it.unit_value),
      ipi: num(it.ipi),
      total_value: num(it.total_value),
    }));
    if (payloadItems.filter((it) => it.action !== "skip").length === 0 && updateStock) {
      toast.error("Nenhum item para importar"); return;
    }
    const invalidExisting = items.some((it) => it.action === "existing" && !it.material_id);
    if (updateStock && invalidExisting) { toast.error("Selecione o material para os itens marcados como 'Vincular'"); return; }
    setImporting(true);
    try {
      const { data } = await axios.post(`${API}/inventory/import-nf`, {
        nf_number: nfNumber,
        nf_date: nfDate,
        supplier: parsed?.supplier || "",
        supplier_cnpj: parsed?.supplier_cnpj || "",
        client_id: clientId,
        items: payloadItems,
        freight: num(freight),
        discount: num(discount),
        icms_st: num(icmsSt),
        nf_total: num(nfTotal) > 0 ? num(nfTotal) : computedTotal,
        update_stock: updateStock,
        create_balance: createBalance,
      }, { withCredentials: true });
      const parts = [];
      if (updateStock) parts.push(`estoque: ${data.created} criados, ${data.updated} atualizados`);
      if (data.balance_created) parts.push("lancamento financeiro registrado");
      toast.success(`NF importada (${parts.join(" | ")})`);
      onImported && onImported();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao importar NF");
    } finally { setImporting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="nf-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Importar Nota Fiscal</DialogTitle>
          <DialogDescription>
            {step === "upload"
              ? "Escolha o cliente, envie o XML da NF-e (leitura exata) ou um PDF/foto (leitura por IA). O sistema le os itens e voce confere antes de confirmar."
              : "Confira itens e valores, escolha se a entrada vai para o estoque e/ou controle de saldo e confirme."}
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="space-y-4 py-2">
            <div className="space-y-1 max-w-sm">
              <Label className="text-xs">Cliente (consignante) *</Label>
              <Select value={clientId || "none"} onValueChange={(v) => setClientId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="nf-client-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Selecione</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.client_code ? `${c.client_code} - ` : ""}{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-lg p-8 cursor-pointer hover:bg-muted/40 transition-colors">
              <input type="file" accept=".xml,.pdf,image/*" className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="nf-file-input" />
              <Upload className="h-8 w-8 text-muted-foreground" />
              {file ? (
                <span className="text-sm font-medium flex items-center gap-2"><FileText className="h-4 w-4" />{file.name}</span>
              ) : (
                <span className="text-sm text-muted-foreground">Clique para selecionar o arquivo (XML, PDF ou imagem)</span>
              )}
            </label>
            <div className="flex justify-end">
              <Button onClick={handleParse} disabled={!file || !clientId || parsing} data-testid="nf-parse-btn">
                {parsing ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Lendo NF...</>) : (<><Sparkles className="h-4 w-4 mr-2" /> Ler NF</>)}
              </Button>
            </div>
          </div>
        )}

        {step === "review" && (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Numero da NF</Label>
                <Input value={nfNumber} onChange={(e) => setNfNumber(e.target.value)} data-testid="nf-number-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Data de Emissao</Label>
                <Input type="date" value={nfDate} onChange={(e) => setNfDate(e.target.value)} data-testid="nf-date-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Fornecedor</Label>
                <Input value={parsed?.supplier || ""} disabled />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">CNPJ Fornecedor</Label>
                <Input value={parsed?.supplier_cnpj || ""} disabled />
              </div>
            </div>

            <div className="border border-border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[200px]">Item da NF</TableHead>
                    <TableHead className="w-[85px]">Qtd</TableHead>
                    <TableHead className="w-[70px]">Un</TableHead>
                    <TableHead className="w-[110px]">Vl. Unit. (R$)</TableHead>
                    <TableHead className="w-[100px]">IPI (R$)</TableHead>
                    <TableHead className="w-[110px]">Total (R$)</TableHead>
                    <TableHead className="w-[130px]">Estoque</TableHead>
                    <TableHead className="min-w-[190px]">Material no estoque</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it, idx) => (
                    <TableRow key={idx} data-testid={`nf-item-${idx}`}>
                      <TableCell className="text-sm">
                        <div className="font-medium">{it.description}</div>
                        {it.code && <div className="text-xs text-muted-foreground font-mono">Cod. forn.: {it.code}</div>}
                      </TableCell>
                      <TableCell>
                        <Input type="number" step="0.01" value={it.quantity}
                          onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                          className="h-8 w-20" data-testid={`nf-item-qty-${idx}`} />
                      </TableCell>
                      <TableCell>
                        <Input value={it.unit} onChange={(e) => updateItem(idx, { unit: e.target.value })} className="h-8 w-14" />
                      </TableCell>
                      <TableCell>
                        <Input type="number" step="0.01" value={it.unit_value}
                          onChange={(e) => updateItem(idx, { unit_value: e.target.value })}
                          className="h-8 w-24" data-testid={`nf-item-unitvalue-${idx}`} />
                      </TableCell>
                      <TableCell>
                        <Input type="number" step="0.01" value={it.ipi}
                          onChange={(e) => updateItem(idx, { ipi: e.target.value })}
                          className="h-8 w-20" data-testid={`nf-item-ipi-${idx}`} />
                      </TableCell>
                      <TableCell>
                        <Input type="number" step="0.01" value={it.total_value}
                          onChange={(e) => updateItem(idx, { total_value: e.target.value })}
                          className="h-8 w-24" data-testid={`nf-item-total-${idx}`} />
                      </TableCell>
                      <TableCell>
                        <Select value={it.action} onValueChange={(v) => updateItem(idx, { action: v, material_id: "" })} disabled={!updateStock}>
                          <SelectTrigger className="h-8" data-testid={`nf-item-action-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">Criar novo</SelectItem>
                            <SelectItem value="existing">Vincular</SelectItem>
                            <SelectItem value="skip">Ignorar</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {!updateStock ? (
                          <span className="text-xs text-muted-foreground">Sem entrada no estoque</span>
                        ) : it.action === "existing" ? (
                          <Select value={it.material_id || "none"} onValueChange={(v) => updateItem(idx, { material_id: v === "none" ? "" : v })}>
                            <SelectTrigger className="h-8" data-testid={`nf-item-material-${idx}`}><SelectValue placeholder="Selecione o material" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Selecione</SelectItem>
                              {clientMaterials.map((m) => <SelectItem key={m.id} value={m.id}>{m.code ? `${m.code} - ` : ""}{m.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : it.action === "new" ? (
                          <Badge variant="secondary">Novo material para o cliente</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">Item ignorado</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {updateStock && clientId && clientMaterials.length === 0 && (
              <p className="text-xs text-muted-foreground">Este cliente ainda nao tem materiais cadastrados — use "Criar novo".</p>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Frete (R$)</Label>
                <Input type="number" step="0.01" value={freight} onChange={(e) => setFreight(e.target.value)} data-testid="nf-freight-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Desconto (R$)</Label>
                <Input type="number" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid="nf-discount-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">ICMS ST (R$)</Label>
                <Input type="number" step="0.01" value={icmsSt} onChange={(e) => setIcmsSt(e.target.value)} data-testid="nf-icmsst-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Valor Total da NF (R$)</Label>
                <Input type="number" step="0.01" value={nfTotal} onChange={(e) => setNfTotal(e.target.value)} data-testid="nf-total-input" />
                <p className="text-[11px] text-muted-foreground">Calculado dos itens: {fmtBRL(computedTotal)}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-6 border border-border rounded-lg p-3 bg-muted/30">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={updateStock} onCheckedChange={(v) => setUpdateStock(!!v)} data-testid="nf-update-stock-checkbox" />
                <span className="text-sm font-medium">Dar entrada no estoque</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={createBalance} onCheckedChange={(v) => setCreateBalance(!!v)} data-testid="nf-create-balance-checkbox" />
                <span className="text-sm font-medium">Registrar no controle de saldo (financeiro)</span>
              </label>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === "review" && (
            <Button variant="outline" onClick={() => setStep("upload")} disabled={importing}>Voltar</Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={importing || parsing}>Cancelar</Button>
          {step === "review" && (
            <Button onClick={handleImport} disabled={importing} data-testid="nf-confirm-import-btn">
              {importing ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importando...</>) : "Confirmar Entrada"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
